import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, Injector, OnDestroy, OnInit, ViewChild, afterNextRender, runInInjectionContext } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import type * as Leaflet from 'leaflet';
import { icon as renderFontAwesomeIcon, IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faArrowsUpDownLeftRight } from '@fortawesome/pro-light-svg-icons/faArrowsUpDownLeftRight';
import { faExpand } from '@fortawesome/pro-light-svg-icons/faExpand';
import { faRotate } from '@fortawesome/pro-light-svg-icons/faRotate';
import { CoordinateEditorComponent } from '@app/shared/components/coordinate-editor/coordinate-editor.component';
import { Guid } from '@app/core/constants/constants';
import { ImageUploadResult } from '@app/shared/components/image-editor/components/image-uploader/image-uploader.component';
import { MapDesignerReferenceLayer } from '@app/shared/components/map-designer/models/map-designer-reference-layer';
import { ImageService } from '@app/shared/services/image.service';
import {
  DEFAULT_OVERLAY_IMAGE_OPACITY,
  OverlayImageConfig,
  OverlayImageCorners,
  RotatedImageOverlay,
  createRotatedImageOverlay,
  latLngToOverlayTuple,
  overlayCornersToLatLng,
  parseOverlayImage,
  serializeOverlayImage
} from '@app/shared/map/overlay-image';
declare const L: typeof Leaflet;
type OverlayHandleType = 'move' | 'resize' | 'rotate';
type MapDesignerInspectorTab = 'image' | 'transform' | 'layers';
const OVERLAY_HANDLE_ICONS: Record<OverlayHandleType, IconDefinition> = {
  move: faArrowsUpDownLeftRight,
  resize: faExpand,
  rotate: faRotate
};

@Component({
    selector: 'app-map-designer-dialog',
    templateUrl: './map-designer-dialog.component.html',
    styleUrls: ['./map-designer-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class MapDesignerDialogComponent implements OnInit, OnDestroy {
  map: Leaflet.Map | undefined;
  drawnItems: Leaflet.FeatureGroup = L.featureGroup();
  referenceLayers: Leaflet.LayerGroup[] = [];
  private referenceOverlayLayers: RotatedImageOverlay[] = [];
  private referenceOverlayConfigs: OverlayImageConfig[] = [];
  private readonly geomanGroupId = 'rtls-editor-layers';
  private readonly geocoderControlPosition: Leaflet.ControlPosition = 'topleft';
  private readonly fallbackMarkerDataUri =
    'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABkAAAApCAYAAADAk4LOAAAFgUlEQVR4Aa1XA5BjWRTN2oW17d3YaZtr2962HUzbDNpjszW24mRt28p47v7zq/bXZtrp/lWnXr337j3nPCe85NcypgSFdugCpW5YoDAMRaIMqRi6aKq5E3YqDQO3qAwjVWrD8Ncq/RBpykd8oZUb/kaJutow8r1aP9II0WmLKLIsJyv1w/kqw9Ch2MYdB++12Onxee/QMwvf4/Dk/Lfp/i4nxTXtOoQ4pW5Aj7wpici1A9erdAN2OH64x8OSP9j3Ft3b7aWkTg/Fm91siTra0f9on5sQr9INejH6CUUUpavjFNq1B+Oadhxmnfa8RfEmN8VNAsQhPqF55xHkMzz3jSmChWU6f7/XZKNH+9+hBLOHYozuKQPxyMPUKkrX/K0uWnfFaJGS1QPRtZsOPtr3NsW0uyh6NNCOkU3Yz+bXbT3I8G3xE5EXLXtCXbbqwCO9zPQYPRTZ5vIDXD7U+w7rFDEoUUf7ibHIR4y6bLVPXrz8JVZEql13trxwue/uDivd3fkWRbS6/IA2bID4uk0UpF1N8qLlbBlXs4Ee7HLTfV1j54APvODnSfOWBqtKVvjgLKzF5YdEk5ewRkGlK0i33Eofffc7HT56jD7/6U+qH3Cx7SBLNntH5YIPvODnyfIXZYRVDPqgHtLs5ABHD3YzLuespb7t79FY34DjMwrVrcTuwlT55YMPvOBnRrJ4VXTdNnYug5ucHLBjEpt30701A3Ts+HEa73u6dT3FNWwflY86eMHPk+Yu+i6pzUpRrW7SNDg5JHR4KapmM5Wv2E8Tfcb1HoqqHMHU+uWDD7zg54mz5/2BSnizi9T1Dg4QQXLToGNCkb6tb1NU+QAlGr1++eADrzhn/u8Q2YZhQVlZ5+CAOtqfbhmaUCS1ezNFVm2imDbPmPng5wmz+gwh+oHDce0eUtQ6OGDIyR0uUhUsoO3vfDmmgOezH0mZN59x7MBi++WDL1g/eEiU3avlidO671bkLfwbw5XV2P8Pzo0ydy4t2/0eu33xYSOMOD8hTf4CrBtGMSoXfPLchX+J0ruSePw3LZeK0juPJbYzrhkH0io7B3k164hiGvawhOKMLkrQLyVpZg8rHFW7E2uHOL888IBPlNZ1FPzstSJM694fWr6RwpvcJK60+0HCILTBzZLFNdtAzJaohze60T8qBzyh5ZuOg5e7uwQppofEmf2++DYvmySqGBuKaicF1blQjhuHdvCIMvp8whTTfZzI7RldpwtSzL+F1+wkdZ2TBOW2gIF88PBTzD/gpeREAMEbxnJcaJHNHrpzji0gQCS6hdkEeYt9DF/2qPcEC8RM28Hwmr3sdNyht00byAut2k3gufWNtgtOEOFGUwcXWNDbdNbpgBGxEvKkOQsxivJx33iow0Vw5S6SVTrpVq11ysA2Rp7gTfPfktc6zhtXBBC+adRLshf6sG2RfHPZ5EAc4sVZ83yCN00Fk/4kggu40ZTvIEm5g24qtU4KjBrx/BTTH8ifVASAG7gKrnWxJDcU7x8X6Ecczhm3o6YicvsLXWfh3Ch1W0k8x0nXF+0fFxgt4phz8QvypiwCCFKMqXCnqXExjq10beH+UUA7+nG6mdG/Pu0f3LgFcGrl2s0kNNjpmoJ9o4B29CMO8dMT4Q5ox8uitF6fqsrJOr8qnwNbRzv6hSnG5wP+64C7h9lp30hKNtKdWjtdkbuPA19nJ7Tz3zR/ibgARbhb4AlhavcBebmTHcFl2fvYEnW0ox9xMxKBS8btJ+KiEbq9zA4RthQXDhPa0T9TEe69gWupwc6uBUphquXgf+/FrIjweHQS4/pduMe5ERUMHUd9xv8ZR98CxkS4F2n3EUrUZ10EYNw7BWm9x1GiPssi3GgiGRDKWRYZfXlON+dfNbM+GgIwYdwAAAAASUVORK5CYII=';
  private styleEditorControl: any;
  private activeLayer: any;
  private initialViewSet = false;
  private geocodeMarker: Leaflet.Marker | null = null;
  private resizeObserver?: ResizeObserver;
  private mapInitializationTimer?: ReturnType<typeof setTimeout>;
  private undoStack: any[] = [];
  private redoStack: any[] = [];
  private undoRedoControl: Leaflet.Control | null = null;
  private guideLines: Leaflet.Polyline[] = [];
  overlayConfig: OverlayImageConfig | null;
  overlayOpacity = DEFAULT_OVERLAY_IMAGE_OPACITY;
  overlayImageUrl: string | null = null;
  activeInspectorTab: MapDesignerInspectorTab = 'image';
  private overlayImageAspectRatio = 1;
  private overlayImageLoadToken = 0;
  private overlayLayer: RotatedImageOverlay | null = null;
  private overlayHandleMarkers: Partial<Record<OverlayHandleType, Leaflet.Marker>> = {};
  @ViewChild(CoordinateEditorComponent) coordinateEditor?: CoordinateEditorComponent;

  constructor(
    public dialogRef: MatDialogRef<MapDesignerDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: {
      referenceGeoJSON: any[];
      referenceLayers: MapDesignerReferenceLayer[];
      editableGeoJSON: any;
      overlayImage: string;
      name: string;
    },
    private injector: Injector,
    private imageService: ImageService,
    private readonly changeDetectorRef: ChangeDetectorRef
  ) {
    this.overlayConfig = parseOverlayImage(this.data.overlayImage);
    this.overlayOpacity = this.overlayConfig?.opacity ?? DEFAULT_OVERLAY_IMAGE_OPACITY;
    this.activeInspectorTab = this.overlayConfig ? 'transform' : 'image';

    this.dialogRef.afterOpened().subscribe(() => {
      setTimeout(() => this.map?.invalidateSize(true), 50);
    });

    runInInjectionContext(this.injector, () => {
      afterNextRender(() => {
        this.mapInitializationTimer = setTimeout(() => {
          this.applyStyleEditorOverrides();
          this.initializeMap();
        });
      });
    });
  }

  ngOnInit(): void {
    // Initialization handled in constructor via runInInjectionContext
  }

  ngOnDestroy(): void {
    if (this.mapInitializationTimer) {
      clearTimeout(this.mapInitializationTimer);
      this.mapInitializationTimer = undefined;
    }
    this.resizeObserver?.disconnect();
    if (this.geocodeMarker) {
      this.geocodeMarker.remove();
      this.geocodeMarker = null;
    }
    this.clearOverlayLayer();
    this.clearReferenceLayers();
    this.map?.remove();
    this.map = undefined;
  }

  initializeMap(): void {
    this.map = L.map('map', {
      center: [40.73, -73.93],
      zoom: 18,
      layers: [
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          maxZoom: 26,
          attribution: ''
        })
      ],
      attributionControl: false
    });

    const referenceOverlayPane = this.map.createPane('referenceOverlayImagePane');
    referenceOverlayPane.style.zIndex = '350';
    referenceOverlayPane.style.pointerEvents = 'none';
    const referenceGeometryPane = this.map.createPane('referenceGeometryPane');
    referenceGeometryPane.style.zIndex = '390';
    referenceGeometryPane.style.pointerEvents = 'none';
    const overlayPane = this.map.createPane('overlayImagePane');
    overlayPane.style.zIndex = '450';
    overlayPane.style.pointerEvents = 'none';
    this.drawnItems.addTo(this.map);

    this.setupGeoman();
    this.setupUndoRedo();
    this.loadReferenceGeoJson();
    this.loadEditableGeoJson();
    this.setupStyleEditor();
    this.setupGeocoder();
    this.observeMapContainer();
    this.loadOverlayImage();
    this.fitInitialMapView();

    this.changeDetectorRef.detectChanges();
    setTimeout(() => this.map?.invalidateSize(true), 100);
  }

  get overlayUploaderImageId(): string {
    return this.overlayConfig?.imageId ?? '';
  }

  get drawnLayerCount(): number {
    return this.drawnItems?.getLayers().length ?? 0;
  }

  get referenceLayerCount(): number {
    return (this.referenceLayers?.length ?? 0) + (this.referenceOverlayLayers?.length ?? 0);
  }

  get overlayAspectRatioLabel(): string {
    return this.overlayConfig?.imageId ? this.getOverlayAspectRatio().toFixed(2) : '-';
  }

  onOverlayImageUploaded(result: ImageUploadResult): void {
    if (!result?.imageId) {
      return;
    }

    const imageId = result.imageId as unknown as string;
    const previousCorners = this.overlayConfig?.corners;
    this.overlayConfig = {
      version: 1,
      imageId,
      opacity: this.overlayOpacity,
      corners: previousCorners ?? this.createDefaultOverlayCorners()
    };
    this.setOverlayImageUrl(imageId, Guid.newGuid(), !previousCorners);
    this.activeInspectorTab = 'transform';
  }

  onOverlayImageRemoved(): void {
    this.removeOverlayImage();
  }

  removeOverlayImage(): void {
    this.overlayConfig = null;
    this.overlayImageUrl = null;
    this.clearOverlayLayer();
    this.activeInspectorTab = 'image';
  }

  setInspectorTab(tab: MapDesignerInspectorTab): void {
    this.activeInspectorTab = tab;
  }

  onInspectorTabKeydown(event: KeyboardEvent): void {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
      return;
    }

    const tabs: MapDesignerInspectorTab[] = ['image', 'transform', 'layers'];
    const currentIndex = tabs.indexOf(this.activeInspectorTab);
    let nextIndex = currentIndex;

    if (event.key === 'Home') {
      nextIndex = 0;
    } else if (event.key === 'End') {
      nextIndex = tabs.length - 1;
    } else {
      const direction = event.key === 'ArrowRight' ? 1 : -1;
      nextIndex = (currentIndex + direction + tabs.length) % tabs.length;
    }

    event.preventDefault();
    this.activeInspectorTab = tabs[nextIndex];
    const tabButtons = (event.currentTarget as HTMLElement).querySelectorAll<HTMLButtonElement>('[role="tab"]');
    requestAnimationFrame(() => tabButtons[nextIndex]?.focus());
  }

  fitOverlayToGeoJson(): void {
    if (!this.overlayConfig) {
      return;
    }

    this.overlayConfig = {
      ...this.overlayConfig,
      corners: this.createDefaultOverlayCorners()
    };
    this.renderOverlayImage();
  }

  overlayOpacityChanged(value: string | number): void {
    const opacity = Math.min(1, Math.max(0, Number(value)));
    this.overlayOpacity = Number.isFinite(opacity) ? opacity : DEFAULT_OVERLAY_IMAGE_OPACITY;
    if (this.overlayConfig) {
      this.overlayConfig = {
        ...this.overlayConfig,
        opacity: this.overlayOpacity
      };
    }
    this.overlayLayer?.setOpacity(this.overlayOpacity);
  }

  private loadOverlayImage(): void {
    if (!this.overlayConfig?.imageId) {
      return;
    }

    this.setOverlayImageUrl(this.overlayConfig.imageId, this.overlayConfig.imageId);
  }

  private getOverlayImageUrl(imageId: string, cacheId: string = imageId): string {
    return this.imageService.getImageUrl(imageId as any, 0, cacheId as any, 2400);
  }

  private setOverlayImageUrl(imageId: string, cacheId: string = imageId, resetCorners = false): void {
    const imageUrl = this.getOverlayImageUrl(imageId, cacheId);
    const loadToken = ++this.overlayImageLoadToken;
    this.overlayImageUrl = imageUrl;

    this.loadOverlayImageAspectRatio(imageUrl).then((aspectRatio) => {
      if (loadToken !== this.overlayImageLoadToken) {
        return;
      }

      this.overlayImageAspectRatio = aspectRatio;
      if (this.overlayConfig) {
        this.overlayConfig = {
          ...this.overlayConfig,
          corners: resetCorners
            ? this.createDefaultOverlayCorners()
            : this.constrainCornersFromTopRight(this.overlayConfig.corners)
        };
      }
      this.renderOverlayImage();
    }).catch(() => {
      if (loadToken !== this.overlayImageLoadToken) {
        return;
      }

      this.renderOverlayImage();
    });
  }

  private loadOverlayImageAspectRatio(imageUrl: string): Promise<number> {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => {
        const aspectRatio = image.naturalWidth > 0 && image.naturalHeight > 0
          ? image.naturalWidth / image.naturalHeight
          : 1;
        resolve(aspectRatio);
      };
      image.onerror = reject;
      image.src = imageUrl;
    });
  }

  private renderOverlayImage(): void {
    this.clearOverlayLayer();

    if (!this.map || !this.overlayConfig || !this.overlayImageUrl) {
      return;
    }

    const constrainedCorners = this.constrainCornersFromTopRight(this.overlayConfig.corners);
    this.overlayConfig = {
      ...this.overlayConfig,
      corners: constrainedCorners
    };

    this.overlayLayer = createRotatedImageOverlay(L, this.overlayImageUrl, constrainedCorners, {
      pane: 'overlayImagePane',
      opacity: this.overlayConfig.opacity ?? this.overlayOpacity,
      interactive: false,
      className: 'map-designer-overlay-image'
    }).addTo(this.map);

    this.createOverlayHandleMarkers();
  }

  private createOverlayHandleMarkers(): void {
    if (!this.map || !this.overlayConfig) {
      return;
    }

    const handles = this.getOverlayHandleLatLngs();
    (Object.keys(handles) as OverlayHandleType[]).forEach((key) => {
      const label = this.getHandleLabel(key);
      const marker = L.marker(handles[key], {
        draggable: true,
        keyboard: true,
        title: label,
        alt: `${label} overlay image`,
        icon: L.divIcon({
          className: `overlay-transform-handle overlay-transform-handle--${key}`,
          html: renderFontAwesomeIcon(OVERLAY_HANDLE_ICONS[key]).html.join(''),
          iconSize: [38, 38],
          iconAnchor: [19, 19]
        })
      }).addTo(this.map as Leaflet.Map);

      marker.bindTooltip(label, {
        direction: 'top',
        offset: L.point(0, -18),
        opacity: 1,
        className: 'overlay-transform-tooltip'
      });
      marker.on('drag', () => this.updateOverlayHandle(key, marker.getLatLng()));
      marker.on('dragend', () => this.updateOverlayHandle(key, marker.getLatLng()));
      this.overlayHandleMarkers[key] = marker;
    });
  }

  private updateOverlayHandle(key: OverlayHandleType, latLng: Leaflet.LatLng): void {
    if (!this.map || !this.overlayConfig) {
      return;
    }

    const corners = this.getTransformedCorners(key, latLng);
    this.overlayConfig = { ...this.overlayConfig, corners };
    this.syncOverlayGeometry();
  }

  private syncOverlayGeometry(): void {
    if (!this.overlayConfig) {
      return;
    }
    const corners = overlayCornersToLatLng(L, this.overlayConfig.corners);
    this.overlayLayer?.setCorners(corners.topLeft, corners.topRight, corners.bottomLeft);
    const handles = this.getOverlayHandleLatLngs();
    this.overlayHandleMarkers.move?.setLatLng(handles.move);
    this.overlayHandleMarkers.resize?.setLatLng(handles.resize);
    this.overlayHandleMarkers.rotate?.setLatLng(handles.rotate);
  }

  private clearOverlayLayer(): void {
    if (this.overlayLayer) {
      this.overlayLayer.remove();
      this.overlayLayer = null;
    }

    Object.values(this.overlayHandleMarkers).forEach((marker) => marker?.remove());
    this.overlayHandleMarkers = {};
  }

  private clearReferenceLayers(): void {
    this.referenceLayers.forEach(layer => layer.remove());
    this.referenceLayers = [];
    this.referenceOverlayLayers.forEach(layer => layer.remove());
    this.referenceOverlayLayers = [];
    this.referenceOverlayConfigs = [];
  }

  private fitInitialMapView(): void {
    if (!this.map) {
      return;
    }

    const bounds = L.latLngBounds([]);
    const extendLayerBounds = (layer: any): void => {
      const layerBounds = typeof layer?.getBounds === 'function' ? layer.getBounds() : null;
      if (layerBounds?.isValid()) {
        bounds.extend(layerBounds);
      }
    };

    extendLayerBounds(this.drawnItems);
    this.referenceLayers.forEach(extendLayerBounds);
    [this.overlayConfig, ...this.referenceOverlayConfigs]
      .filter((config): config is OverlayImageConfig => !!config)
      .forEach(config => {
        const corners = overlayCornersToLatLng(L, config.corners);
        bounds.extend(corners.topLeft);
        bounds.extend(corners.topRight);
        bounds.extend(corners.bottomLeft);
        bounds.extend(L.latLng(
          corners.topRight.lat + corners.bottomLeft.lat - corners.topLeft.lat,
          corners.topRight.lng + corners.bottomLeft.lng - corners.topLeft.lng
        ));
      });

    if (bounds.isValid()) {
      this.map.fitBounds(bounds.pad(0.08), { maxZoom: 22 });
      this.initialViewSet = true;
    }
  }

  private createDefaultOverlayCorners(): OverlayImageCorners {
    const bounds = this.resolveOverlayFitBounds();
    if (this.map) {
      const northWest = this.map.latLngToLayerPoint(bounds.getNorthWest());
      const southEast = this.map.latLngToLayerPoint(bounds.getSouthEast());
      const boxWidth = Math.abs(southEast.x - northWest.x);
      const boxHeight = Math.abs(southEast.y - northWest.y);
      const boxAspectRatio = boxHeight > 0 ? boxWidth / boxHeight : this.getOverlayAspectRatio();
      const overlayAspectRatio = this.getOverlayAspectRatio();
      const width = boxAspectRatio > overlayAspectRatio ? boxHeight * overlayAspectRatio : boxWidth;
      const height = width / overlayAspectRatio;
      const center = L.point(
        (northWest.x + southEast.x) / 2,
        (northWest.y + southEast.y) / 2
      );
      const topLeft = L.point(center.x - width / 2, center.y - height / 2);
      const topRight = L.point(center.x + width / 2, center.y - height / 2);
      const bottomLeft = L.point(center.x - width / 2, center.y + height / 2);

      return {
        topLeft: latLngToOverlayTuple(this.map.layerPointToLatLng(topLeft)),
        topRight: latLngToOverlayTuple(this.map.layerPointToLatLng(topRight)),
        bottomLeft: latLngToOverlayTuple(this.map.layerPointToLatLng(bottomLeft))
      };
    }

    return {
      topLeft: [bounds.getNorth(), bounds.getWest()],
      topRight: [bounds.getNorth(), bounds.getEast()],
      bottomLeft: [bounds.getSouth(), bounds.getWest()]
    };
  }

  private getTransformedCorners(key: OverlayHandleType, latLng: Leaflet.LatLng): OverlayImageCorners {
    if (!this.map || !this.overlayConfig) {
      return this.overlayConfig?.corners ?? this.createDefaultOverlayCorners();
    }

    const geometry = this.getOverlayGeometryPoints();
    if (!geometry) {
      return this.overlayConfig.corners;
    }

    const draggedPoint = this.map.latLngToLayerPoint(latLng);

    if (key === 'move') {
      const delta = draggedPoint.subtract(geometry.center);
      return this.overlayCornersFromPoints(
        geometry.topLeft.add(delta),
        geometry.topRight.add(delta),
        geometry.bottomLeft.add(delta)
      );
    }

    if (key === 'resize') {
      const currentDiagonal = geometry.bottomRight.subtract(geometry.topLeft);
      const draggedDiagonal = draggedPoint.subtract(geometry.topLeft);
      const currentDiagonalLengthSq = Math.max(1, currentDiagonal.x * currentDiagonal.x + currentDiagonal.y * currentDiagonal.y);
      const scale = Math.max(
        0.05,
        (draggedDiagonal.x * currentDiagonal.x + draggedDiagonal.y * currentDiagonal.y) / currentDiagonalLengthSq
      );
      const widthVector = geometry.widthVector.multiplyBy(scale);
      const heightVector = geometry.heightVector.multiplyBy(scale);
      return this.overlayCornersFromPoints(
        geometry.topLeft,
        geometry.topLeft.add(widthVector),
        geometry.topLeft.add(heightVector)
      );
    }

    const rotateVector = draggedPoint.subtract(geometry.center);
    const rotateLength = Math.hypot(rotateVector.x, rotateVector.y);
    if (rotateLength < 8) {
      return this.overlayConfig.corners;
    }

    const heightUnit = L.point(-rotateVector.x / rotateLength, -rotateVector.y / rotateLength);
    const widthUnit = L.point(heightUnit.y, -heightUnit.x);
    const widthVector = widthUnit.multiplyBy(geometry.width);
    const heightVector = heightUnit.multiplyBy(geometry.height);
    const topLeft = L.point(
      geometry.center.x - (widthVector.x + heightVector.x) / 2,
      geometry.center.y - (widthVector.y + heightVector.y) / 2
    );

    return this.overlayCornersFromPoints(
      topLeft,
      topLeft.add(widthVector),
      topLeft.add(heightVector)
    );
  }

  private constrainCornersFromTopRight(corners: OverlayImageCorners): OverlayImageCorners {
    if (!this.map) {
      return corners;
    }

    const latLngCorners = overlayCornersToLatLng(L, corners);
    return this.constrainCornersFromTopRightPoint(
      this.map.latLngToLayerPoint(latLngCorners.topLeft),
      this.map.latLngToLayerPoint(latLngCorners.topRight)
    );
  }

  private constrainCornersFromTopRightPoint(topLeft: Leaflet.Point, topRight: Leaflet.Point): OverlayImageCorners {
    const widthVector = topRight.subtract(topLeft);
    const width = Math.max(1, Math.hypot(widthVector.x, widthVector.y));
    const height = width / this.getOverlayAspectRatio();
    const bottomLeft = L.point(
      topLeft.x - (widthVector.y / width) * height,
      topLeft.y + (widthVector.x / width) * height
    );

    return this.overlayCornersFromPoints(topLeft, topRight, bottomLeft);
  }

  private constrainCornersFromBottomLeftPoint(topLeft: Leaflet.Point, bottomLeft: Leaflet.Point): OverlayImageCorners {
    const heightVector = bottomLeft.subtract(topLeft);
    const height = Math.max(1, Math.hypot(heightVector.x, heightVector.y));
    const width = height * this.getOverlayAspectRatio();
    const topRight = L.point(
      topLeft.x + (heightVector.y / height) * width,
      topLeft.y - (heightVector.x / height) * width
    );

    return this.overlayCornersFromPoints(topLeft, topRight, bottomLeft);
  }

  private getOverlayGeometryPoints(): {
    topLeft: Leaflet.Point;
    topRight: Leaflet.Point;
    bottomLeft: Leaflet.Point;
    bottomRight: Leaflet.Point;
    center: Leaflet.Point;
    widthVector: Leaflet.Point;
    heightVector: Leaflet.Point;
    width: number;
    height: number;
  } | null {
    if (!this.map || !this.overlayConfig) {
      return null;
    }

    const corners = overlayCornersToLatLng(L, this.overlayConfig.corners);
    const topLeft = this.map.latLngToLayerPoint(corners.topLeft);
    const topRight = this.map.latLngToLayerPoint(corners.topRight);
    const bottomLeft = this.map.latLngToLayerPoint(corners.bottomLeft);
    const widthVector = topRight.subtract(topLeft);
    const heightVector = bottomLeft.subtract(topLeft);
    const bottomRight = topRight.add(heightVector);
    const center = L.point(
      topLeft.x + (widthVector.x + heightVector.x) / 2,
      topLeft.y + (widthVector.y + heightVector.y) / 2
    );

    return {
      topLeft,
      topRight,
      bottomLeft,
      bottomRight,
      center,
      widthVector,
      heightVector,
      width: Math.max(1, Math.hypot(widthVector.x, widthVector.y)),
      height: Math.max(1, Math.hypot(heightVector.x, heightVector.y))
    };
  }

  private getOverlayHandleLatLngs(): Record<OverlayHandleType, Leaflet.LatLng> {
    const geometry = this.getOverlayGeometryPoints();
    if (!this.map || !geometry) {
      const fallback = this.map?.getCenter() ?? L.latLng(0, 0);
      return {
        move: fallback,
        resize: fallback,
        rotate: fallback
      };
    }

    const height = geometry.height;
    const heightUnit = L.point(
      geometry.heightVector.x / height,
      geometry.heightVector.y / height
    );
    const topCenter = L.point(
      (geometry.topLeft.x + geometry.topRight.x) / 2,
      (geometry.topLeft.y + geometry.topRight.y) / 2
    );
    const rotateHandle = L.point(
      topCenter.x - heightUnit.x * 44,
      topCenter.y - heightUnit.y * 44
    );

    return {
      move: this.map.layerPointToLatLng(geometry.center),
      resize: this.map.layerPointToLatLng(geometry.bottomRight),
      rotate: this.map.layerPointToLatLng(rotateHandle)
    };
  }

  private overlayCornersFromPoints(topLeft: Leaflet.Point, topRight: Leaflet.Point, bottomLeft: Leaflet.Point): OverlayImageCorners {
    if (!this.map) {
      return this.overlayConfig?.corners ?? {
        topLeft: [0, 0],
        topRight: [0, 0],
        bottomLeft: [0, 0]
      };
    }

    return {
      topLeft: latLngToOverlayTuple(this.map.layerPointToLatLng(topLeft)),
      topRight: latLngToOverlayTuple(this.map.layerPointToLatLng(topRight)),
      bottomLeft: latLngToOverlayTuple(this.map.layerPointToLatLng(bottomLeft))
    };
  }

  private getOverlayAspectRatio(): number {
    return Number.isFinite(this.overlayImageAspectRatio) && this.overlayImageAspectRatio > 0
      ? this.overlayImageAspectRatio
      : 1;
  }

  private resolveOverlayFitBounds(): Leaflet.LatLngBounds {
    const drawnBounds = this.drawnItems.getBounds();
    if (drawnBounds?.isValid()) {
      return drawnBounds.pad(0.02);
    }

    const referenceBounds = this.referenceLayers.reduce<Leaflet.LatLngBounds | null>((acc, layer) => {
      const bounds = typeof (layer as any).getBounds === 'function' ? (layer as any).getBounds() : null;
      if (!bounds?.isValid()) {
        return acc;
      }
      return acc ? acc.extend(bounds) : bounds;
    }, null);

    if (referenceBounds?.isValid()) {
      return referenceBounds.pad(0.02);
    }

    return this.map?.getBounds().pad(-0.18) ?? L.latLngBounds([40.729, -73.931], [40.731, -73.929]);
  }

  private getHandleLabel(key: OverlayHandleType): string {
    if (key === 'move') {
      return 'Move';
    }
    if (key === 'resize') {
      return 'Size';
    }
    return 'Rotate';
  }

  private observeMapContainer(): void {
    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    const mapElement = document.getElementById('map');
    if (!mapElement) {
      return;
    }

    this.resizeObserver?.disconnect();
    this.resizeObserver = new ResizeObserver(() => {
      this.map?.invalidateSize();
    });
    this.resizeObserver.observe(mapElement);
  }

  private setupGeoman(): void {
    const geoman = (this.map as any).pm;
    if (!geoman) {
      return;
    }

    geoman.setGlobalOptions({
      allowSelfIntersection: true,
      rotate: true,
      draggable: true,
      snappable: true,
      snapDistance: 10,
      snapMiddle: true,
      snapSegment: true,
      requireSnapToFinish: false,
      layerGroup: this.drawnItems,
      measurements: { measurement: true, displayFormat: 'metric' },
      continueDrawing: false,
      editable: true,
      templineStyle: {
        color: '#808080',
        weight: 2
      },
      hintlineStyle: {
        color: '#ff6b6b',
        dashArray: '5,5',
        weight: 2
      },
      pathOptions: {
        color: '#808080',
        fillColor: '#808080',
        fillOpacity: 0.2,
        weight: 2
      }
    });

    geoman.addControls({
      position: 'topleft',
      drawControls: true,
      editControls: true,
      customControls: true,
      drawPolygon: true,
      drawRectangle: true,
      drawCircle: true,
      drawPolyline: true,
      drawMarker: true,
      drawCircleMarker: true,
      drawText: false,
      editMode: true,
      dragMode: true,
      rotateMode: true,
      cutPolygon: true,
      removalMode: true,
      snapMode: true
    });

    // Duplicate butonu ekle
    const DuplicateControl = L.Control.extend({
      options: { position: 'topleft' },
      onAdd: (map: Leaflet.Map) => {
        const container = L.DomUtil.create('div', 'leaflet-bar leaflet-control');
        const button = L.DomUtil.create('a', 'leaflet-pm-icon-duplicate', container);
        button.href = '#';
        button.title = 'Seçili şekli kopyala';
        button.setAttribute('role', 'button');

        // Inline style ekle (Angular encapsulation bypass)
        button.style.cssText = `
          display: block !important;
          width: 30px !important;
          height: 30px !important;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%230f172a' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Crect x='9' y='9' width='13' height='13' rx='2' ry='2'/%3E%3Cpath d='M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1'/%3E%3C/svg%3E") !important;
          background-size: 18px 18px !important;
          background-repeat: no-repeat !important;
          background-position: center !important;
          cursor: pointer !important;
        `;

        button.addEventListener('mouseenter', () => {
          button.style.backgroundColor = '#f9fafb';
        });

        button.addEventListener('mouseleave', () => {
          button.style.backgroundColor = '';
        });

        L.DomEvent.on(button, 'click', (e: Event) => {
          L.DomEvent.stopPropagation(e);
          L.DomEvent.preventDefault(e);
          this.duplicateActiveLayer();
        });

        return container;
      }
    });

    new DuplicateControl().addTo(this.map);

    this.map?.on('pm:create', (event: any) => {
      const layer = event?.layer;
      if (layer) {
        this.saveState();
        this.registerLayer(layer);
        this.makeLayerActive(layer, true, true);

        // Mesafe bilgisi ekle
        this.addMeasurementToLayer(layer);
      }
    });

    this.map?.on('pm:remove', (event: any) => {
      const layer = event?.layer;
      if (layer && this.drawnItems.hasLayer(layer)) {
        this.saveState();
        this.drawnItems.removeLayer(layer);
        if (this.activeLayer === layer) {
          this.activeLayer = this.drawnItems.getLayers()[0] ?? null;
          this.openStyleEditorPanel(this.activeLayer ?? null);
        }
      }
    });

    // Edit, drag, rotate bitince state kaydet ve measurement güncelle
    this.map?.on('pm:edit', (e: any) => {
      this.saveState();
      if (e.layer) {
        this.addMeasurementToLayer(e.layer);
      }
    });
    this.map?.on('pm:drag', () => this.saveState());
    this.map?.on('pm:rotate', () => this.saveState());
    this.map?.on('pm:cut', (e: any) => {
      this.saveState();
      if (e.layer) {
        this.addMeasurementToLayer(e.layer);
      }
    });

    // Draw mode başladığında guide çizgileri göster
    this.map?.on('pm:drawstart', () => {
      this.showGuideLines();
    });

    // Draw mode bitince guide çizgileri gizle
    this.map?.on('pm:drawend', () => {
      this.hideGuideLines();
    });

    // Global Drag mode toggle
    this.map?.on('pm:globaldragmodetoggled', (e: any) => {
      this.openStyleEditorPanel();
      if (e.enabled) {
        this.showGuideLines();
      } else {
        this.hideGuideLines();
      }
    });

    // Tek layer drag olayları
    this.map?.on('pm:dragstart', () => {
      this.showGuideLines();
    });

    this.map?.on('pm:dragend', () => {
      // Global mode hala aktifse guide'ları gizleme
      const pm = (this.map as any).pm;
      if (!pm.globalDragModeEnabled()) {
        this.hideGuideLines();
      }
    });

    // Global Rotate mode toggle
    this.map?.on('pm:globalrotatemodetoggled', (e: any) => {
      this.openStyleEditorPanel();
      if (e.enabled) {
        this.showGuideLines();
      } else {
        this.hideGuideLines();
      }
    });

    // Tek layer rotate olayları
    this.map?.on('pm:rotatestart', () => {
      this.showGuideLines();
    });

    this.map?.on('pm:rotateend', () => {
      // Global mode hala aktifse guide'ları gizleme
      const pm = (this.map as any).pm;
      if (!pm.globalRotateModeEnabled()) {
        this.hideGuideLines();
      }
    });

    // Edit mode başladığında (scale/vertex edit) guide'ları aktif et
    this.map?.on('pm:globaleditmodetoggled', (e: any) => {
      this.openStyleEditorPanel();
      if (e.enabled) {
        this.showGuideLines();
      } else {
        this.hideGuideLines();
      }
    });

    // Vertex drag olayları (node düzenleme)
    this.map?.on('pm:markerdragstart', () => {
      this.showGuideLines();
    });

    this.map?.on('pm:markerdragend', () => {
      // Edit mode hala aktifse guide'ları gizleme
      const pm = (this.map as any).pm;
      if (!pm.globalEditModeEnabled()) {
        this.hideGuideLines();
      }
    });

    // Mouse hareket ederken guide çizgileri güncelle
    this.map?.on('mousemove', (e: any) => {
      const pm = (this.map as any).pm;
      const isDragMode = pm.globalDragModeEnabled();
      const isRotateMode = pm.globalRotateModeEnabled();
      const isEditMode = pm.globalEditModeEnabled();
      const isDrawMode = pm.globalDrawModeEnabled();

      if (isDrawMode || isDragMode || isRotateMode || isEditMode) {
        this.updateGuideLines(e.latlng);
      }
    });

    if ((L.control as any)?.fullscreen) {
      (L.control as any).fullscreen({ position: 'topleft' }).addTo(this.map);
    }
  }

  private loadReferenceGeoJson(): void {
    (this.data.referenceGeoJSON ?? []).forEach(geoJSON => this.addReferenceGeoJson(geoJSON));

    (this.data.referenceLayers ?? []).forEach(reference => {
      this.addReferenceGeoJson(reference.geoJSON);
      this.addReferenceOverlay(reference);
    });
  }

  private addReferenceGeoJson(geoJSON: unknown): void {
    if (!geoJSON || !this.isValidGeoJSON(geoJSON)) {
      return;
    }

    try {
      const referenceOptions = {
        pane: 'referenceGeometryPane',
        interactive: false,
        pmIgnore: true,
        snapIgnore: false
      };
      const referenceLayer = L.geoJSON(geoJSON as any, {
        ...referenceOptions,
        style: (feature: any) => ({
          ...this.getReferenceStyle(feature),
          ...referenceOptions
        }),
        pointToLayer: (_feature, latLng) => L.circleMarker(latLng, {
          ...referenceOptions,
          radius: 5,
          fillOpacity: 0.45
        } as any)
      } as any);

      this.referenceLayers.push(referenceLayer);
      referenceLayer.addTo(this.map as L.Map);

      referenceLayer.eachLayer((layer: any) => {
        layer.options.pmIgnore = true;
        layer.options.snapIgnore = false;
        layer.options.interactive = false;
        layer.off?.();
        try {
          layer.pm?.disable?.();
          layer.pm?.disableLayerDrag?.();
        } catch (error) {
          console.warn('Unable to disable editing on reference layer', error);
        }
      });
    } catch (error) {
      console.error('Unable to render reference GeoJSON.', error);
    }
  }

  private addReferenceOverlay(reference: MapDesignerReferenceLayer): void {
    if (!this.map) {
      return;
    }

    const config = parseOverlayImage(reference.overlayImage);
    if (!config?.imageId) {
      return;
    }

    const layer = createRotatedImageOverlay(
      L,
      this.getOverlayImageUrl(config.imageId),
      config.corners,
      {
        pane: 'referenceOverlayImagePane',
        opacity: config.opacity ?? DEFAULT_OVERLAY_IMAGE_OPACITY,
        interactive: false,
        className: `map-designer-reference-overlay map-designer-reference-overlay--${reference.relation}`
      }
    ).addTo(this.map);

    this.referenceOverlayConfigs.push(config);
    this.referenceOverlayLayers.push(layer);
  }

  private loadEditableGeoJson(): void {
    if (!this.data.editableGeoJSON || !this.isValidGeoJSON(this.data.editableGeoJSON)) {
      if (this.data.editableGeoJSON) {
        console.error('Invalid editableGeoJSON data:', this.data.editableGeoJSON);
      }
      return;
    }

    try {
      L.geoJSON(this.data.editableGeoJSON, {
        pointToLayer: (feature, latlng) => {
          // Circle için özel işlem
          if (feature.properties && feature.properties.radius) {
            return L.circle(latlng, { radius: feature.properties.radius });
          }
          // CircleMarker için özel işlem
          if (feature.properties && feature.properties.circleMarker) {
            return L.circleMarker(latlng, { radius: feature.properties.circleMarkerRadius || 10 });
          }
          // Normal marker
          return L.marker(latlng);
        },
        style: this.getFeatureStyle,
        onEachFeature: (_feature, layer) => this.registerLayer(layer, _feature)
      });
    } catch (error) {
      console.error('An error occurred:', error);
    }
  }

  private setupStyleEditor(): void {
    if (typeof (L.control as any)?.styleEditor !== 'function') {
      console.warn('Leaflet style editor control is unavailable. Skipping style editor setup.');
      return;
    }

    this.styleEditorControl = this.styleEditorControl ?? (L.control as any).styleEditor({
      position: 'topleft',
      useGrouping: false,
      openOnLeafletDraw: false
    }).addTo(this.map);

    const initialLayer = this.activeLayer ?? this.drawnItems.getLayers()[0] ?? null;
    if (initialLayer) {
      setTimeout(() => this.openStyleEditorPanel(initialLayer, true), 0);
    }

    this.map?.on('styleeditor:changed', (event: any) => {
      const layer = event?.layer ?? event;
      if (layer) {
        this.updateGeoJSONLayer(layer);
      }
    });

    this.drawnItems.eachLayer((layer: any) => this.registerLayer(layer, layer.feature));
  }

  private registerLayer(layer: any, feature?: any): void {
    if (!layer) {
      return;
    }

    if (!this.drawnItems.hasLayer(layer)) {
      this.drawnItems.addLayer(layer);
    }

    this.applyFeatureStyle(layer, feature ?? layer.toGeoJSON());
    this.updateGeoJSONLayer(layer);
    this.enableLayerEditing(layer);

    if (!(layer as any)._pmHooksApplied) {
      layer.on('styleeditor:changed', () => this.updateGeoJSONLayer(layer));
      layer.on('click', () => this.makeLayerActive(layer, false, false));
      layer.on('pm:edit', () => this.refreshLayer(layer));
      layer.on('pm:vertexadded', () => this.refreshLayer(layer));
      layer.on('pm:vertexremoved', () => this.refreshLayer(layer));
      layer.on('pm:rotate', () => this.refreshLayer(layer));
      layer.on('pm:drag', (e: any) => {
        this.refreshLayer(layer);
        // Drag sırasında guide'ları güncelle
        const latlng = e.latlng || (layer.getLatLng && layer.getLatLng()) ||
          (layer.getBounds && layer.getBounds().getCenter());
        if (latlng && this.map) {
          this.updateGuideLines(latlng);
        }
      });
      layer.on('pm:dragend', () => this.refreshLayer(layer));

      // Vertex drag için guide line
      layer.on('pm:markerdrag', (e: any) => {
        const latlng = e.latlng || e.markerEvent?.latlng;
        if (latlng && this.map) {
          this.updateGuideLines(latlng);
        }
      });

      (layer as any)._pmHooksApplied = true;
    }

    this.makeLayerActive(layer, !this.initialViewSet, false);
  }

  private enableLayerEditing(layer: any): void {
    const pm = layer?.pm;
    if (!pm) {
      return;
    }

    const pmOptions = {
      allowSelfIntersection: false,
      rotate: false,
      draggable: true,
      snappable: true,
      layerGroup: this.drawnItems
    };

    try {
      if (typeof pm.addToGroup === 'function') {
        pm.addToGroup(this.geomanGroupId);
      }

      if (typeof pm.setOptions === 'function') {
        pm.setOptions(pmOptions);
      }

      if (typeof pm.disable === 'function') {
        pm.disable();
      }

      if (typeof pm.enableLayerDrag === 'function') {
        pm.enableLayerDrag();
      }

      if (typeof pm.enable === 'function') {
        pm.enable();
      }
    } catch (error) {
      console.warn('Failed to configure Geoman for layer', error);
    }
  }

  private makeLayerActive(layer: any, focus = true, ensureSelection = false): void {
    if (!layer) {
      return;
    }

    const isSameLayer = this.activeLayer === layer;
    this.activeLayer = layer;

    if (focus) {
      this.focusOnLayer(layer);
    }

    if (isSameLayer && !ensureSelection) {
      return;
    }

    this.openStyleEditorPanel(layer, ensureSelection);
  }

  private refreshLayer(layer: any): void {
    this.updateGeoJSONLayer(layer);
    this.openStyleEditorPanel(layer);
  }

  private focusOnLayer(layer: any): void {
    if (!this.map || !layer) {
      return;
    }

    const bounds = typeof layer.getBounds === 'function' ? layer.getBounds() : null;
    if (bounds && bounds.isValid()) {
      this.map.fitBounds(bounds.pad(0.08));
      this.initialViewSet = true;
      return;
    }

    const latLng = typeof layer.getLatLng === 'function' ? layer.getLatLng() : null;
    if (latLng) {
      this.map.setView(latLng, Math.max(this.map.getZoom(), 18));
      this.initialViewSet = true;
    }
  }

  private openStyleEditorPanel(layer?: any, ensureSelection = false): void {
    if (!this.styleEditorControl) {
      return;
    }

    const targetLayer = layer ?? this.activeLayer ?? this.drawnItems.getLayers()[0];
    if (!targetLayer) {
      return;
    }

    this.activeLayer = targetLayer;

    try {
      if (typeof this.styleEditorControl.disable === 'function') {
        this.styleEditorControl.disable();
      }
      if (typeof this.styleEditorControl.enable === 'function') {
        this.styleEditorControl.enable(targetLayer);
      }
    } catch (error) {
      console.warn('Unable to open style editor', error);
    }
  }

  getFeatureStyle(feature: any): any {
    const properties = feature?.properties ?? {};
    return {
      color: properties.stroke || '#808080',
      weight: properties['stroke-width'] || 2,
      opacity: properties['stroke-opacity'] ?? 1,
      fillColor: properties.fill || '#808080',
      fillOpacity: properties['fill-opacity'] ?? 0.2,
      dashArray: properties['stroke-dasharray'] || null,
      lineCap: properties['stroke-linecap'] || null,
      lineJoin: properties['stroke-linejoin'] || null,
      dashOffset: properties['stroke-dashoffset'] || null,
      fillRule: properties['fill-rule'] || null,
      pattern: properties['stroke-pattern'] || null
    };
  }

  private getReferenceStyle(feature: any): any {
    const baseStyle = this.getFeatureStyle(feature);
    const referenceType = feature?.properties?.__referenceType;
    const referenceRelation = feature?.properties?.__referenceRelation;

    if (referenceType === 'zone' || referenceRelation === 'sibling') {
      return {
        ...baseStyle,
        color: '#0891b2',
        weight: 1.5,
        opacity: 0.7,
        fillOpacity: 0.1,
        dashArray: null
      };
    }

    return {
      ...baseStyle,
      color: '#4b5563',
      weight: 1,
      opacity: 0.35,
      fillOpacity: 0.04,
      dashArray: '4 4'
    };
  }

  private ensureGeocodeMarker(): Leaflet.Marker {
    if (!this.map) {
      throw new Error('Map not initialised');
    }

    if (!this.geocodeMarker) {
      this.geocodeMarker = L.marker([0, 0], {
        icon: L.divIcon({
          className: 'search-result-marker',
          html: '<div class="search-result-pin"></div>',
          iconSize: [24, 24],
          iconAnchor: [12, 24]
        })
      }).addTo(this.map);
    } else if (!this.geocodeMarker.getPane()) {
      this.geocodeMarker.addTo(this.map);
    }

    return this.geocodeMarker;
  }
  private focusOnLocation(lat: number, lon: number, label?: string): void {
    if (!this.map || Number.isNaN(lat) || Number.isNaN(lon)) {
      return;
    }

    const target = L.latLng(lat, lon);
    this.map.flyTo(target, Math.max(this.map.getZoom() ?? 0, 18));

    const marker = this.ensureGeocodeMarker();
    marker.setLatLng(target);
    if (label) {
      marker.bindPopup(label, { autoPan: true }).openPopup();
    } else {
      marker.closePopup();
    }

    if (this.coordinateEditor) {
      this.coordinateEditor.value = [lat, lon];
    }
  }

  private applyFeatureStyle(layer: any, feature: any): void {
    if (!feature) {
      return;
    }

    const featureData = feature.type ? feature : { properties: feature.properties };
    if (layer.setStyle) {
      layer.setStyle(this.getFeatureStyle(featureData));
    }

    if (layer.setRadius && featureData.properties?.radius) {
      layer.setRadius(featureData.properties.radius);
    }
  }

  private updateGeoJSONLayer(layer: any): void {
    const geoJSONObj = this.layerToGeoJSON(layer);

    if (layer.feature) {
      layer.feature = {
        ...geoJSONObj,
        properties: { ...layer.feature.properties, ...geoJSONObj.properties }
      };
    } else {
      layer.feature = geoJSONObj;
    }
  }

  private layerToGeoJSON(layer: any): any {
    const geoJSONObj = layer.toGeoJSON();
    if (!geoJSONObj.properties) {
      geoJSONObj.properties = {};
    }
    geoJSONObj.properties.name = this.data.name;

    geoJSONObj.properties.stroke = layer.options.color;
    geoJSONObj.properties['stroke-width'] = layer.options.weight;
    geoJSONObj.properties['stroke-opacity'] = layer.options.opacity;
    geoJSONObj.properties.fill = layer.options.fillColor;
    geoJSONObj.properties['fill-opacity'] = layer.options.fillOpacity;
    geoJSONObj.properties['stroke-dasharray'] = layer.options.dashArray;
    geoJSONObj.properties['stroke-linecap'] = layer.options.lineCap;
    geoJSONObj.properties['stroke-linejoin'] = layer.options.lineJoin;
    geoJSONObj.properties['stroke-dashoffset'] = layer.options.dashOffset;
    geoJSONObj.properties['fill-rule'] = layer.options.fillRule;
    geoJSONObj.properties['stroke-pattern'] = layer.options.pattern;

    // Radius için özel işlem (Circle ve CircleMarker)
    if (layer.getRadius) {
      const radius = layer.getRadius();
      geoJSONObj.properties.radius = radius;

      // CircleMarker mı yoksa Circle mı kontrol et
      if (layer instanceof L.CircleMarker && !(layer instanceof L.Circle)) {
        geoJSONObj.properties.circleMarker = true;
        geoJSONObj.properties.circleMarkerRadius = radius;
      }
    }

    return geoJSONObj;
  }

  onSave(): void {
    const updatedGeoJSON = this.buildFeatureCollection();

    if (!updatedGeoJSON.features.length) {
      console.warn('No drawable features found to serialise.');
    }

    if (!this.isValidGeoJSON(updatedGeoJSON)) {
      console.error('Invalid GeoJSON data:', updatedGeoJSON);
      return;
    }

    this.dialogRef.close({
      status: true,
      data: updatedGeoJSON,
      overlayImage: serializeOverlayImage(this.overlayConfig),
      name: this.data.name
    });
  }

  private buildFeatureCollection(): any {
    const collection = {
      type: 'FeatureCollection',
      features: [] as any[],
      properties: { name: this.data.name }
    };

    this.drawnItems.eachLayer((layer: any) => {
      const geoJSONObj = this.layerToGeoJSON(layer);

      if (this.isFeature(geoJSONObj)) {
        collection.features.push(this.ensureFeatureName(geoJSONObj));
        return;
      }

      if (this.isFeatureCollection(geoJSONObj)) {
        geoJSONObj.features.forEach((feature: any) => {
          if (this.isFeature(feature)) {
            collection.features.push(this.ensureFeatureName(feature));
          }
        });
      }
    });

    return collection;
  }

  private isFeature(geoJSON: any): boolean {
    return geoJSON?.type === 'Feature' && !!geoJSON.geometry;
  }

  private isFeatureCollection(geoJSON: any): boolean {
    return geoJSON?.type === 'FeatureCollection' && Array.isArray(geoJSON.features);
  }

  private ensureFeatureName(feature: any): any {
    feature.properties = {
      ...(feature.properties ?? {}),
      name: this.data.name
    };
    return feature;
  }

  private isValidGeoJSON(geoJSON: any): boolean {
    if (!geoJSON || typeof geoJSON !== 'object') {
      return false;
    }

    const validTypes = ['Point', 'MultiPoint', 'LineString', 'MultiLineString', 'Polygon', 'MultiPolygon', 'GeometryCollection', 'Feature', 'FeatureCollection'];
    if (!validTypes.includes(geoJSON.type)) {
      return false;
    }

    if (geoJSON.type === 'FeatureCollection' && !Array.isArray(geoJSON.features)) {
      return false;
    }

    return true;
  }

  coordinateChanged(newCoordinates: number[]): void {
    const mapCenter = L.latLng(newCoordinates[0], newCoordinates[1]);
    this.map?.setView(mapCenter, 16);
  }

  private applyStyleEditorOverrides(): void {
    const styleEditor = (window as any)?.L?.StyleEditor;
    if (!styleEditor?.marker) {
      return;
    }

    const fallbackUrl = this.resolveDefaultMarkerUrl();

    const defaultMarker = styleEditor.marker.DefaultMarker?.prototype;
    if (defaultMarker && !defaultMarker.__rtlsPatched) {
      defaultMarker.__rtlsPatched = true;
      const fallbackResolver = () => fallbackUrl;
      defaultMarker._getMarkerUrl = fallbackResolver;
      defaultMarker._getMarkerUrlForStyle = fallbackResolver;
    }

    const glyphiconMarker = styleEditor.marker.GlyphiconMarker?.prototype;
    if (glyphiconMarker && !glyphiconMarker.__rtlsPatched) {
      glyphiconMarker.__rtlsPatched = true;
      const fallbackResolver = () => fallbackUrl;
      glyphiconMarker._getMarkerUrl = fallbackResolver;
      glyphiconMarker._getMarkerUrlForStyle = fallbackResolver;
    }
  }

  private resolveDefaultMarkerUrl(): string {
    const defaultOptions: any = (L.Icon.Default as any)?.prototype?.options ?? L.Icon.Default.prototype.options;
    if (defaultOptions?.iconUrl) {
      return defaultOptions.iconUrl;
    }

    if (defaultOptions) {
      defaultOptions.iconUrl = this.fallbackMarkerDataUri;
      defaultOptions.iconRetinaUrl = this.fallbackMarkerDataUri;
    }

    const imagePath: string | undefined = (L.Icon.Default as any).imagePath;
    if (imagePath) {
      return `${imagePath.replace(/\/$/, '')}/marker-icon.png`;
    }

    return this.fallbackMarkerDataUri;
  }

  private setupUndoRedo(): void {
    if (!this.map) {
      return;
    }

    // Keyboard shortcuts
    document.addEventListener('keydown', (e) => {
      // Ctrl+Z veya Cmd+Z için undo
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        this.undo();
      }
      // Ctrl+Y veya Cmd+Shift+Z için redo
      if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        this.redo();
      }
    });

    // Control oluştur - bottom center'da yan yana
    const UndoRedoControl = L.Control.extend({
      options: { position: 'bottomleft' },

      onAdd: (map: Leaflet.Map) => {
        const container = L.DomUtil.create('div', 'leaflet-bar leaflet-control undo-redo-center');
        container.style.cssText = 'display: flex; flex-direction: row;';

        const undoBtn = L.DomUtil.create('a', '', container);
        undoBtn.href = '#';
        undoBtn.title = 'Geri al (Ctrl+Z)';
        undoBtn.innerHTML = '↶';
        undoBtn.style.cssText = 'display: flex; align-items: center; justify-content: center; width: 34px; height: 34px; font-size: 20px; border-right: 1px solid #ccc;';

        const redoBtn = L.DomUtil.create('a', '', container);
        redoBtn.href = '#';
        redoBtn.title = 'İleri al (Ctrl+Y)';
        redoBtn.innerHTML = '↷';
        redoBtn.style.cssText = 'display: flex; align-items: center; justify-content: center; width: 34px; height: 34px; font-size: 20px;';

        L.DomEvent.disableClickPropagation(container);

        L.DomEvent.on(undoBtn, 'click', (e: any) => {
          L.DomEvent.preventDefault(e);
          L.DomEvent.stopPropagation(e);
          this.undo();
        });

        L.DomEvent.on(redoBtn, 'click', (e: any) => {
          L.DomEvent.preventDefault(e);
          L.DomEvent.stopPropagation(e);
          this.redo();
        });

        // Buton referanslarını sakla
        container.dataset.undoBtn = '0';
        container.dataset.redoBtn = '1';

        return container;
      }
    });

    this.undoRedoControl = new UndoRedoControl();
    this.undoRedoControl.addTo(this.map);

    // İlk durumu kaydet ve butonları güncelle
    setTimeout(() => {
      this.saveState();
      this.updateUndoRedoButtons();
    }, 500);
  }

  private saveState(): void {
    // Mevcut durumu kaydet
    const state = this.buildFeatureCollection();
    this.undoStack.push(JSON.parse(JSON.stringify(state)));

    // Redo stack'i temizle
    this.redoStack = [];

    // Stack boyutunu sınırla (max 50 adım)
    if (this.undoStack.length > 50) {
      this.undoStack.shift();
    }

    this.updateUndoRedoButtons();
  }

  private undo(): void {
    if (this.undoStack.length <= 1) {
      console.log('Geri alınacak işlem yok');
      return;
    }

    // Mevcut durumu redo stack'e kaydet
    const currentState = this.undoStack.pop();
    if (currentState) {
      this.redoStack.push(currentState);
    }

    // Bir önceki durumu yükle
    const previousState = this.undoStack[this.undoStack.length - 1];
    if (previousState) {
      this.restoreState(previousState);
    }

    this.updateUndoRedoButtons();
  }

  private redo(): void {
    if (this.redoStack.length === 0) {
      console.log('İleri alınacak işlem yok');
      return;
    }

    // Redo stack'ten durumu al
    const nextState = this.redoStack.pop();
    if (nextState) {
      this.undoStack.push(nextState);
      this.restoreState(nextState);
    }

    this.updateUndoRedoButtons();
  }

  private addMeasurementToLayer(layer: any): void {
    if (!layer) return;

    // Mevcut tooltip'i kaldır
    if ((layer as any)._measurementTooltip) {
      (layer as any)._measurementTooltip.remove();
      (layer as any)._measurementTooltip = null;
    }

    let measurementText = '';

    // Circle için
    if (layer instanceof L.Circle) {
      const radius = layer.getRadius();
      const area = Math.PI * radius * radius;
      measurementText = `Yarıçap: ${this.formatDistance(radius)}<br>Alan: ${this.formatArea(area)}`;
    }
    // Polygon için
    else if (layer instanceof L.Polygon && !(layer instanceof L.Rectangle)) {
      const area = this.calculatePolygonArea(layer);
      measurementText = `Alan: ${this.formatArea(area)}`;
    }
    // Rectangle için
    else if (layer instanceof L.Rectangle) {
      const area = this.calculatePolygonArea(layer);
      measurementText = `Alan: ${this.formatArea(area)}`;
    }
    // Polyline için
    else if (layer instanceof L.Polyline && !(layer instanceof L.Polygon)) {
      const length = this.calculatePolylineLength(layer);
      measurementText = `Uzunluk: ${this.formatDistance(length)}`;
    }

    // Tooltip ekle
    if (measurementText) {
      const tooltip = L.tooltip({
        permanent: true,
        direction: 'center',
        className: 'leaflet-pm-measurement-tooltip'
      }).setContent(measurementText);

      layer.bindTooltip(tooltip);
      (layer as any)._measurementTooltip = tooltip;
    }
  }

  private calculatePolylineLength(layer: Leaflet.Polyline): number {
    const latlngs = layer.getLatLngs() as L.LatLng[];
    let totalDistance = 0;

    for (let i = 0; i < latlngs.length - 1; i++) {
      totalDistance += latlngs[i].distanceTo(latlngs[i + 1]);
    }

    return totalDistance;
  }

  private calculatePolygonArea(layer: Leaflet.Polygon | Leaflet.Rectangle): number {
    const latlngs = layer.getLatLngs()[0] as L.LatLng[];
    if (!latlngs || latlngs.length < 3) return 0;

    // Shoelace formula for polygon area (approximate for small areas)
    let area = 0;
    const radius = 6371000; // Earth radius in meters

    for (let i = 0; i < latlngs.length; i++) {
      const j = (i + 1) % latlngs.length;
      const lat1 = latlngs[i].lat * Math.PI / 180;
      const lat2 = latlngs[j].lat * Math.PI / 180;
      const lng1 = latlngs[i].lng * Math.PI / 180;
      const lng2 = latlngs[j].lng * Math.PI / 180;

      area += (lng2 - lng1) * (2 + Math.sin(lat1) + Math.sin(lat2));
    }

    area = Math.abs(area * radius * radius / 2);
    return area;
  }

  private formatDistance(meters: number): string {
    if (meters < 1000) {
      return `${meters.toFixed(2)} m`;
    }
    return `${(meters / 1000).toFixed(2)} km`;
  }

  private formatArea(squareMeters: number): string {
    if (squareMeters < 10000) {
      return `${squareMeters.toFixed(2)} m²`;
    }
    return `${(squareMeters / 10000).toFixed(2)} ha`;
  }

  private showGuideLines(): void {
    // Guide çizgileri zaten varsa göster
    this.guideLines.forEach(line => {
      if (this.map && !this.map.hasLayer(line)) {
        line.addTo(this.map);
      }
    });
  }

  private hideGuideLines(): void {
    // Guide çizgilerini gizle
    this.guideLines.forEach(line => {
      if (this.map && this.map.hasLayer(line)) {
        this.map.removeLayer(line);
      }
    });
  }

  private duplicateActiveLayer(): void {
    if (!this.activeLayer || !this.map) {
      console.warn('Kopyalanacak aktif bir şekil seçilmedi');
      return;
    }

    try {
      // Layer'ı GeoJSON'a çevir
      const geoJSON = this.activeLayer.toGeoJSON();

      // Offset ekle (10 metre sağa ve aşağı kaydır)
      const offsetLat = 0.00009; // ~10 metre
      const offsetLng = 0.00009;

      if (geoJSON.geometry.type === 'Point') {
        geoJSON.geometry.coordinates[0] += offsetLng;
        geoJSON.geometry.coordinates[1] += offsetLat;
      } else if (geoJSON.geometry.type === 'LineString' || geoJSON.geometry.type === 'MultiLineString') {
        const coords = geoJSON.geometry.coordinates;
        coords.forEach((coord: any) => {
          if (Array.isArray(coord[0])) {
            // MultiLineString
            coord.forEach((c: any) => {
              c[0] += offsetLng;
              c[1] += offsetLat;
            });
          } else {
            // LineString
            coord[0] += offsetLng;
            coord[1] += offsetLat;
          }
        });
      } else if (geoJSON.geometry.type === 'Polygon' || geoJSON.geometry.type === 'MultiPolygon') {
        const coords = geoJSON.geometry.coordinates;
        coords.forEach((ring: any) => {
          ring.forEach((coord: any) => {
            if (Array.isArray(coord[0])) {
              // MultiPolygon veya iç ring
              coord.forEach((c: any) => {
                c[0] += offsetLng;
                c[1] += offsetLat;
              });
            } else {
              // Normal Polygon ring
              coord[0] += offsetLng;
              coord[1] += offsetLat;
            }
          });
        });
      }

      // Yeni layer oluştur
      const newLayer = L.geoJSON(geoJSON, {
        pointToLayer: (feature, latlng) => {
          // Circle için
          if (feature.properties && feature.properties.radius) {
            return L.circle(latlng, { radius: feature.properties.radius });
          }
          // CircleMarker için
          if (feature.properties && feature.properties.circleMarker) {
            return L.circleMarker(latlng, { radius: feature.properties.circleMarkerRadius || 10 });
          }
          return L.marker(latlng);
        },
        style: (feature) => this.getFeatureStyle(feature)
      });

      // Yeni layer'ı ekle
      newLayer.eachLayer((layer: any) => {
        this.drawnItems.addLayer(layer);
        this.registerLayer(layer, geoJSON);

        // Ölçüm ekle
        this.addMeasurementToLayer(layer);

        // Yeni layer'ı aktif yap
        this.activeLayer = layer;
        this.openStyleEditorPanel(layer, false);
      });

      // State kaydet
      this.saveState();

      console.log('Şekil kopyalandı');
    } catch (error) {
      console.error('Şekil kopyalanırken hata oluştu:', error);
    }
  }

  private updateGuideLines(mouseLatLng: Leaflet.LatLng): void {
    if (!this.map) return;

    // Önceki guide çizgilerini temizle
    this.hideGuideLines();
    this.guideLines = [];

    const snapThreshold = 0.00002; // Yaklaşık 2 metre guide gösterme mesafesi
    const snapDistance = 5; // 5 piksel snap mesafesi
    const allVertices: { lat: number; lng: number }[] = [];

    // Tüm layer'ların vertex'lerini topla
    this.drawnItems.eachLayer((layer: any) => {
      if (layer.getLatLngs) {
        const latlngs = layer.getLatLngs();
        const vertices = Array.isArray(latlngs[0]) ? latlngs[0] : latlngs;
        vertices.forEach((v: Leaflet.LatLng) => {
          allVertices.push({ lat: v.lat, lng: v.lng });
        });
      } else if (layer.getLatLng) {
        const v = layer.getLatLng();
        allVertices.push({ lat: v.lat, lng: v.lng });
      }
    });

    const bounds = this.map.getBounds();
    const mapNorth = bounds.getNorth();
    const mapSouth = bounds.getSouth();
    const mapEast = bounds.getEast();
    const mapWest = bounds.getWest();

    // Mouse pozisyonunu pixel cinsinden al
    const mousePoint = this.map.latLngToContainerPoint(mouseLatLng);
    let snappedLat: number | null = null;
    let snappedLng: number | null = null;

    // Horizontal guide çizgiler (lat bazlı) ve snap
    allVertices.forEach(vertex => {
      if (Math.abs(vertex.lat - mouseLatLng.lat) < snapThreshold) {
        // Guide line çiz
        const line = L.polyline(
          [[vertex.lat, mapWest], [vertex.lat, mapEast]],
          {
            color: '#ff6b6b',
            weight: 2,
            dashArray: '10, 5',
            opacity: 0.7,
            interactive: false
          }
        );
        this.guideLines.push(line);
        line.addTo(this.map);

        // Snap kontrolü - pixel mesafesi
        const vertexPoint = this.map.latLngToContainerPoint(L.latLng(vertex.lat, mouseLatLng.lng));
        const pixelDistance = Math.abs(mousePoint.y - vertexPoint.y);

        if (pixelDistance < snapDistance) {
          snappedLat = vertex.lat;
        }
      }
    });

    // Vertical guide çizgiler (lng bazlı) ve snap
    allVertices.forEach(vertex => {
      if (Math.abs(vertex.lng - mouseLatLng.lng) < snapThreshold) {
        // Guide line çiz
        const line = L.polyline(
          [[mapSouth, vertex.lng], [mapNorth, vertex.lng]],
          {
            color: '#ff6b6b',
            weight: 2,
            dashArray: '10, 5',
            opacity: 0.7,
            interactive: false
          }
        );
        this.guideLines.push(line);
        line.addTo(this.map);

        // Snap kontrolü - pixel mesafesi
        const vertexPoint = this.map.latLngToContainerPoint(L.latLng(mouseLatLng.lat, vertex.lng));
        const pixelDistance = Math.abs(mousePoint.x - vertexPoint.x);

        if (pixelDistance < snapDistance) {
          snappedLng = vertex.lng;
        }
      }
    });

    // Snap uygulandıysa mouse pozisyonunu güncelle
    if (snappedLat !== null || snappedLng !== null) {
      const newLat = snappedLat !== null ? snappedLat : mouseLatLng.lat;
      const newLng = snappedLng !== null ? snappedLng : mouseLatLng.lng;
      const snappedLatLng = L.latLng(newLat, newLng);

      // Geoman'a snapped pozisyonu bildir
      const pm = (this.map as any).pm;
      if (pm._currentMarker) {
        pm._currentMarker.setLatLng(snappedLatLng);
      }
    }
  }

  private restoreState(state: any): void {
    if (!state || !this.map) {
      return;
    }

    // Mevcut layer'ları temizle
    this.drawnItems.clearLayers();

    // State'i yükle
    if (state.features && state.features.length > 0) {
      this.data.editableGeoJSON = state;
      this.loadEditableGeoJson();
    }
  }

  private updateUndoRedoButtons(): void {
    if (!this.undoRedoControl) return;

    const container = (this.undoRedoControl as any).getContainer?.();
    if (!container) return;

    const buttons = container.getElementsByTagName('a');
    const undoBtn = buttons[0];
    const redoBtn = buttons[1];

    if (undoBtn) {
      if (this.undoStack.length <= 1) {
        undoBtn.style.opacity = '0.3';
        undoBtn.style.cursor = 'not-allowed';
      } else {
        undoBtn.style.opacity = '1';
        undoBtn.style.cursor = 'pointer';
      }
    }

    if (redoBtn) {
      if (this.redoStack.length === 0) {
        redoBtn.style.opacity = '0.3';
        redoBtn.style.cursor = 'not-allowed';
      } else {
        redoBtn.style.opacity = '1';
        redoBtn.style.cursor = 'pointer';
      }
    }
  }

  private setupGeocoder(): void {
    if (!this.map) {
      return;
    }

    const leafletControl: any = L.Control as any;
    const geocoderFactory = leafletControl?.geocoder;
    if (typeof geocoderFactory !== 'function') {
      console.warn('Leaflet geocoder control is unavailable. Skipping geocoder setup.');
      return;
    }

    const geocoder = geocoderFactory({
      defaultMarkGeocode: false,
      placeholder: 'Konum ara...',
      position: 'topright',
      geocoder: leafletControl?.Geocoder?.nominatim?.({
        geocodingQueryParams: {
          'accept-language': 'tr,en',
          'addressdetails': 1,
          'extratags': 1,
          'namedetails': 1,
          'limit': 10
        },
        serviceUrl: 'https://nominatim.openstreetmap.org/',
        htmlTemplate: (r: any) => {
          const name = r.name || r.address?.amenity || r.address?.shop || r.address?.tourism || '';
          const addr = r.address || {};
          const parts = [
            addr.road || addr.street,
            addr.quarter || addr.neighbourhood,
            addr.suburb,
            addr.city || addr.town || addr.village,
            addr.state || addr.province,
            addr.country
          ].filter(p => p && p !== name);

          const addressStr = parts.length > 0 ? parts.join(', ') : '';
          return name + (addressStr ? ' - ' + addressStr : '');
        }
      })
    });

    geocoder.on('markgeocode', (event: any) => {
      const geocode = event?.geocode;
      if (!geocode?.center) {
        return;
      }

      const { lat, lng } = geocode.center;
      let label = typeof geocode.html === 'string' ? geocode.html : geocode.name;
      label = undefined;// don't show label popup
      this.focusOnLocation(lat, lng, label);
    });

    geocoder.addTo(this.map);
  }
}
